import { TableView } from '@components/table';
import { ThemeVariant } from '@components/theme-provider';

import { CompanyUserFavorite } from 'api/favorites/requests';
import { request } from 'api/request';
import { Workspace } from 'api/workspaces/requests';

export type LoginData = {
  email: string;
  password: string;
};

export const login = async (data: LoginData) => request.post('v1/auth/login', data);

export const loginAzure = async (data: { token: string; email: string }) =>
  request.post('v1/auth/login/azure/', data);

export const logout = async () => request.post('v1/auth/logout');

export type Feature =
  | 'FEATURE_ASK_AI'
  | 'FEATURE_AI_AGENTS'
  | 'FEATURE_AI_BUILDER'
  | 'FEATURE_AI_CONTENT'
  | 'FEATURE_JOURNEY_LOOP'
  | 'FEATURE_INTEGRATION_WEBHOOKS'
  | 'FEATURE_CALENDAR_ACTION'
  | 'FEATURE_TIME_SAVED'
  | 'FEATURE_CUSTOM_PORTAL_DOMAIN'
  | 'FEATURE_AZURE_SSO'
  | 'FEATURE_SMS'
  | 'FEATURE_CODE_ACTION'
  | 'FEATURE_MCP_SERVERS'
  | 'FEATURE_KENNITALA';

export type CompanyUserOptions = {
  hasHiddenEmailDomainWizard: boolean;
  hasHiddenIntegrationWizard: boolean;
  hasHiddenAskAIField: boolean;
  hasHiddenAskAIPrompt: boolean;
  hasHiddenUpdateFieldsSelectField: boolean;
  hasHiddenAddToJourneySelectJourney: boolean;
  hasHiddenAddToJourneyFields: boolean;
  hasHiddenUpdateTravellerSelectField: boolean;
  hasHiddenAiAgentInstructions: boolean;
  navbarWorkspacesOrder: Array<Workspace['shortId']>;
  navbarFavoritesOrder: Array<CompanyUserFavorite['shortId']>;
} & {
  [key in `tableViewFor${string}`]?: TableView;
};

type Steps = {
  createAccount?: boolean;
  runDemoJourney?: boolean;
  explorePortal?: boolean;
  exploreTemplates?: boolean;
  publishJourney?: boolean;
  inviteTeammate?: boolean;
};

export type StepKey = keyof Steps;

type Company = {
  shortId: string;
  id: number;
  name: string;
  logo: string;
  features?: Feature[];
  isDemoCompany: boolean;
  slug: string;
  portalDomain: string | null;
  hasFinishedPreboarding: boolean;
  hasFinishedInitializing: boolean;
  industry: string;
  employeeCount: string;
  isPaydayCustomer: boolean;
  onboarding?: {
    isSkipped: boolean;
    isCompleted: boolean;
    status: {
      config: {
        travellerJourneyId: string;
        journeyId: string;
        homeWorkspaceId: string;
      };
      steps: Steps;
    };
    ratio: number;
  };
  hasIntegrations: boolean;
};

type CompanyMinimal = {
  shortId: string;
  id: number;
  name: string;
};

export type PlanCurrencies = 'USD' | 'EUR' | 'GBP';

export type PlanFrequencies = 'monthly' | 'yearly';

type PlanTypes =
  | 'trial'
  | 'business'
  | 'premium'
  | 'enterprise'
  | 'demo_internal'
  | 'demo_external';

type Plan = {
  planType: PlanTypes;
  downgradeToPlanType: PlanTypes | null;
  planCurrency: PlanCurrencies;
  creditsPerPeriod: number;
  dateToReset: Date | null;
  dateToClose: string | null;
  creditsUsed: number;
};

export type CurrentUser = null | {
  id: number;
  shortId: string;
  companies: CompanyMinimal[];
  company: Company;
  name: string;
  email: string;
  role: 'admin';
  options: Partial<CompanyUserOptions>;
  picture?: string;
  isSystemUser: boolean;
  hasFinishedPreboarding: boolean;
  hasFinishedOnboarding: boolean;
  theme: ThemeVariant;
  plan: Plan | null;
};

export const getCurrentUser = async () => {
  const response = await request.get<CurrentUser>('v1/auth/whoami');

  if (response.data) {
    window.heap?.addUserProperties({
      buildDate: window.buildDate,
      shortId: response.data.shortId,
      isSystemUser: response.data.isSystemUser,
      company: response.data.company.name,
      companyShortId: response.data.company.shortId,
      isDemoCompany: response.data.company.isDemoCompany,
    });
  }

  return response.data;
};

export type UserApp = {
  clientId: string;
  clientName: string;
  description: string | null;
  hasFullAccess: boolean;
  scopes: string[];
};
export type UserAppResponse = UserApp & {
  clientSecret: string;
};

export const loadUserApps = async () => {
  const response = await request.get<UserApp[]>('v1/auth/user-app/');
  return response.data;
};

export const createUserApp = async (data: Omit<UserApp, 'clientId'>) => {
  const response = await request.post<UserAppResponse>('v1/auth/user-app/', data);
  return response.data;
};

export const updateUserApp = async ({ clientId, ...updateData }: UserApp) => {
  const response = await request.patch(`v1/auth/user-app/${clientId}/`, updateData);
  return response.data;
};

export const deleteUserApp = async (clientId: string) => {
  const response = await request.delete(`v1/auth/user-app/${clientId}/`);
  return response.data;
};

export const switchCompany = async (company: CompanyMinimal) => {
  const response = await request.post(`v1/auth/switch/${company.id}`);
  return response.data;
};

type UpdateUserOptionsData = {
  key: keyof CompanyUserOptions;
  value: any;
};

export const updateUserOptions = async (data: UpdateUserOptionsData) => {
  const response = await request.patch('v1/users/options/update/', data);
  return response.data;
};
